import {Component} from '@angular/core';
import {fromEvent, merge, of} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {NavigationComponent, NavigationSectionGroup} from './navigation.component';

@Component({
    selector: '[appNavigation=scroll]',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
})
export class ScrollNavigationComponent extends NavigationComponent {
    private _scrollParent: Element;
    private _placeholder: HTMLElement | null = null;

    private getActualScrollParent(node: Element): Element {
        let current = node.parentElement;
        while (current) {
            const style = getComputedStyle(current);
            const overflowY = style.overflowY;
            // position !== 'static' distinguishes .panel (position:absolute, overflow-y:auto)
            // from .panel-body (position:static, overflow-x:auto which forces overflow-y:auto via CSS quirk)
            if ((overflowY === 'auto' || overflowY === 'scroll') && style.position !== 'static') {
                return current;
            }
            current = current.parentElement;
        }
        return (document.scrollingElement as Element) || document.documentElement;
    }

    get offset() {
        return this.navCntRef ? this.navCntRef.nativeElement.getBoundingClientRect().bottom : 0;
    }

    private updateStickyState() {
        if (!this.navCntRef) return;

        const navEl: HTMLElement = this.navCntRef.nativeElement;
        const spRect = this._scrollParent.getBoundingClientRect();

        if (this._placeholder) {
            // Currently fixed — check if we should unfix
            const placeholderRect = this._placeholder.getBoundingClientRect();
            if (placeholderRect.top >= spRect.top) {
                this._placeholder.remove();
                this._placeholder = null;
                navEl.style.position = '';
                navEl.style.top = '';
                navEl.style.left = '';
                navEl.style.width = '';
            } else {
                // Update position in case of resize — use placeholder for left/width (original nav position)
                navEl.style.top = `${spRect.top}px`;
                navEl.style.left = `${placeholderRect.left}px`;
                navEl.style.width = `${placeholderRect.width}px`;
            }
        } else {
            // Not fixed — check if we should fix
            const navRect = navEl.getBoundingClientRect();
            if (navRect.top < spRect.top) {
                this._placeholder = document.createElement('div');
                this._placeholder.style.height = `${navRect.height}px`;
                navEl.parentNode.insertBefore(this._placeholder, navEl);

                navEl.style.position = 'fixed';
                navEl.style.top = `${spRect.top}px`;
                // Use nav's own left/width (not scroll parent's) to preserve panel-body padding alignment
                navEl.style.left = `${navRect.left}px`;
                navEl.style.width = `${navRect.width}px`;
            }
        }
    }

    ngAfterViewInit() {
        this._scrollParent = this.getActualScrollParent(this.elRef.nativeElement);
        // Reset scroll position so freshly rendered content always starts at the top.
        // The panel element persists across tab changes and retains its scrollTop.
        this._scrollParent.scrollTop = 0;

        merge(
            of(null),
            this.sections$,
            fromEvent(this._scrollParent, 'scroll'),
            fromEvent(window, 'resize'),
        ).pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.updateStickyState();

            if (!this.navCntRef) return;

            const offset = this.offset;
            const sections = this.sections;
            if (!sections.length) return;

            const nextI = sections.findIndex(x => x.sections[0].elRef.nativeElement.getBoundingClientRect().y >= offset + 1);
            // When nextI = -1 (all sections above threshold) but scroll is at top, default to first section
            const atTop = (this._scrollParent as HTMLElement).scrollTop === 0;
            const i = nextI < 0 ? (atTop ? 0 : sections.length - 1) : Math.max(0, nextI - 1);

            if (this.activeSection !== sections[i]) {
                this.activeSection = sections[i];

                const navEl: any = Array.from(this.navCntRef.nativeElement.querySelectorAll('.nav-item'))[i];
                navEl?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
            }
        });

        this.cdr.detectChanges();
    }

    jumpTo(section: NavigationSectionGroup) {
        const el: HTMLElement = section.sections[0].elRef.nativeElement;
        const elRect = el.getBoundingClientRect();
        const spRect = this._scrollParent.getBoundingClientRect();
        const navHeight = this.navCntRef ? this.navCntRef.nativeElement.getBoundingClientRect().height : 0;
        this._scrollParent.scrollTo({
            top: this._scrollParent.scrollTop + elRect.top - spRect.top - navHeight,
            behavior: 'smooth',
        });
    }

    compOnDestroy() {
        if (this._placeholder) {
            this._placeholder.remove();
            this._placeholder = null;
        }
        super.compOnDestroy();
    }
}
